import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAutoShipments, getSubscriptionItems, getSubscriptionTbrItems } from '../services/subscriptionService';
import { AutoShipmentItem, SubscriptionItem, TbrItem } from '../types';
import { RootState } from './store';

type LoadingState = 'not-asked' | 'loading' | 'done' | 'error';

interface State {
  customerNumber: string;
  autoShipmentItems: AutoShipmentItem[];
  subscriptionItems: SubscriptionItem[];
  tbrItems: TbrItem[];
  loadingState: LoadingState;
}

const initialState: State = {
  customerNumber: '',
  autoShipmentItems: [],
  subscriptionItems: [],
  tbrItems: [],
  loadingState: 'not-asked'
};

export const getCustomerAutoShipmentItems = createAsyncThunk<AutoShipmentItem[], string>(
  'getCustomerAutoShipmentItems',
  async (customerNumber: string) => {
    const response = await getAutoShipments(customerNumber);
    return response;
  }
);

export const getCustomerSubscriptionItems = createAsyncThunk<SubscriptionItem[], string>(
  'getCustomerSubscriptionItems',
  async (customerNumber: string) => {
    const response = await getSubscriptionItems(customerNumber);
    return response;
  }
);

export const getTbrItems = createAsyncThunk('getTbrItems', async () => {
  const response = await getSubscriptionTbrItems();
  return response;
});

export const getSubscriptionData = createAsyncThunk('getSubscriptionData', async (customerNumber: string, { dispatch }) => {
  await Promise.all([
    dispatch(getCustomerAutoShipmentItems(customerNumber)),
    dispatch(getCustomerSubscriptionItems(customerNumber)),
    dispatch(getTbrItems())
  ]);
  return customerNumber;
});

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(getSubscriptionData.pending, (state) => {
      state.loadingState = 'loading';
    });
    builder.addCase(getSubscriptionData.fulfilled, (state, action) => {
      state.loadingState = 'done';
      state.customerNumber = action.payload;
    });
    builder.addCase(getSubscriptionData.rejected, (state) => {
      state.loadingState = 'error';
    });
    builder.addCase(getCustomerAutoShipmentItems.fulfilled, (state, action) => {
      state.autoShipmentItems = action.payload;
    });
    builder.addCase(getCustomerSubscriptionItems.fulfilled, (state, action) => {
      state.subscriptionItems = action.payload;
    });
    builder.addCase(getTbrItems.fulfilled, (state, action) => {
      state.tbrItems = action.payload;
    });
  }
});

export const selectAutoShipmentItems = (state: RootState) => state.subscription.autoShipmentItems;

export const selectSubscriptionItems = (state: RootState) => state.subscription.subscriptionItems;

export const selectTimeBasedTbrItems = (state: RootState) => state.subscription.tbrItems.filter((tbrItem) => tbrItem.RefillType.trim() === 'TimeBase');

export const selectSubscriptionCustomerNumber = (state: RootState) => state.subscription.customerNumber;

export const selectLoadingState = (state: RootState) => state.subscription.loadingState;

export default subscriptionSlice.reducer;
